import { Box, Typography, Button, styled, Stack, RadioGroup, FormControlLabel, Radio, Skeleton } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import { useState, useEffect } from 'react';

import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import { WithoutHeader, DesktopLayout } from 'src/layout';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import useUserStore from 'src/stores/user';
import { AIIcon } from 'src/icons/AIIcon';
import AccountsService from 'src/services/UserSevice';
import CustomSwitch from 'src/components/CustomSwitch';
import Tooltip from '@mui/material/Tooltip';
import EventService from 'src/services/EventService';
import Title from 'src/components/Title';

const CustomTextField = styled(Box)(({ theme }) => ({
    backgroundColor: 'var(--lm-surface)',
    borderRadius: '16px',
}));

// Компонент скелетона для блока подключения к Telegram
const TelegramConnectionSkeleton = () => (
    <Box
        mt={2}
        mb={2}
        p={2}
        bgcolor="var(--lm-surface)"
        borderRadius={3}
        boxShadow={0}
        display="flex"
        flexDirection="row"
        alignItems="flex-start"
        gap={2}
    >
        <Box
            flex={1}
            display="flex"
            flexDirection="column"
            gap={1}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
            >
                <Skeleton
                    variant="text"
                    width="80%"
                    height={32}
                    sx={{ bgcolor: 'var(--lm-canvas)' }}
                />
                <Skeleton
                    variant="rectangular"
                    width={120}
                    height={36}
                    sx={{ borderRadius: 2, bgcolor: 'var(--lm-canvas)' }}
                />
            </Box>
            <Box
                display="flex"
                alignItems="center"
                gap={1}
                mb={1}
            >
                <Skeleton
                    variant="rectangular"
                    width={40}
                    height={20}
                    sx={{ borderRadius: 1, bgcolor: 'var(--lm-canvas)' }}
                />
                <Skeleton
                    variant="text"
                    width={150}
                    height={20}
                    sx={{ bgcolor: 'var(--lm-canvas)' }}
                />
            </Box>
            <Box
                display="flex"
                gap={2}
            >
                <Skeleton
                    variant="rectangular"
                    width={120}
                    height={20}
                    sx={{ borderRadius: 1, bgcolor: 'var(--lm-canvas)' }}
                />
                <Skeleton
                    variant="rectangular"
                    width={180}
                    height={20}
                    sx={{ borderRadius: 1, bgcolor: 'var(--lm-canvas)' }}
                />
            </Box>
        </Box>
    </Box>
);

export default function NotificationsScreen() {
    const { enqueueSnackbar } = useSnackbar();
    const isMobile = useIsMobileSize();

    const userInfo = useUserStore((state) => ({
        tg_notification_mode: state.tg_notification_mode || 'all',
        tg_notifications_enabled: state.tg_notifications_enabled,
        is_telegram_connected: state.is_telegram_connected,
        current_subscription: state.current_subscription,
    }));

    const [tgNotificationMode, setTgNotificationMode] = useState<'all' | 'relevant'>(
        userInfo.tg_notification_mode || 'all'
    );
    const [isTelegramConnected, setIsTelegramConnected] = useState(false);
    const [notificationsEnabled, setNotificationsEnabled] = useState(false);
    const [isLoadingTelegramStatus, setIsLoadingTelegramStatus] = useState(true);

    const updateModeMutation = useMutation(
        (mode: 'all' | 'relevant') => AccountsService.updateAccount({ tg_notification_mode: mode }),
        {
            onSuccess: () => {
                enqueueSnackbar('Настройки уведомлений обновлены', { variant: 'success' });
            },
            onError: () => {
                enqueueSnackbar('Ошибка при обновлении настроек', { variant: 'error' });
            },
        }
    );

    const getBindLinkMutation = useMutation(() => AccountsService.getTelegramBindLink(), {
        onError: () => {
            enqueueSnackbar('Ошибка при получении ссылки', { variant: 'error' });
        },
    });

    const handleChangeMode = async (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
        const mode = value as 'all' | 'relevant';
        const previousMode = tgNotificationMode;
        setTgNotificationMode(mode);
        try {
            await updateModeMutation.mutateAsync(mode);

            // Обновляем глобальный стор для синхронизации
            useUserStore.getState().updateNotificationSettings({
                tg_notification_mode: mode,
            });
        } catch (error) {
            // Возвращаем состояние обратно при ошибке
            setTgNotificationMode(previousMode);
            console.error('Ошибка обновления режима уведомлений:', error);
            // Ошибка уже обработана в onError мутации
        }
    };

    const handleConnectBot = async () => {
        try {
            const res = await getBindLinkMutation.mutateAsync();
            if (res && typeof res.telegram_bind_link === 'string') {
                if (typeof res.is_telegram_connected === 'boolean') {
                    setIsTelegramConnected(res.is_telegram_connected);
                }
                window.open(res.telegram_bind_link, '_blank', 'noopener,noreferrer');
            } else {
                enqueueSnackbar('Ошибка при получении ссылки', { variant: 'error' });
            }
        } catch {
            // Ошибка уже обработана в onError
        }
    };

    // Проверяем доступность AI опции
    const isAIAvailable =
        userInfo.current_subscription?.plan?.ai_analysis_enabled && userInfo.current_subscription?.is_active;

    // Синхронизируем локальный стейт с хранилищем при обновлении страницы
    useEffect(() => {
        setTgNotificationMode(userInfo.tg_notification_mode);
    }, [userInfo.tg_notification_mode]);

    useEffect(() => {
        setNotificationsEnabled(!!userInfo.tg_notifications_enabled);
    }, [userInfo.tg_notifications_enabled]);

    useEffect(() => {
        (async () => {
            try {
                const res = await AccountsService.getTelegramBindLink();
                if (res && typeof res.is_telegram_connected === 'boolean') {
                    setIsTelegramConnected(res.is_telegram_connected);
                }
            } catch {
            } finally {
                setIsLoadingTelegramStatus(false);
            }
        })();
    }, []);

    const renderTelegramBlock = () => {
        const canSelectMode = isTelegramConnected === true && notificationsEnabled === true;
        if (isLoadingTelegramStatus) {
            return <TelegramConnectionSkeleton />;
        }

        return (
            <Box
                mt={2}
                mb={2}
                p={2}
                bgcolor="var(--lm-surface)"
                borderRadius={3}
                boxShadow={0}
                display="flex"
                flexDirection="row"
                alignItems="flex-start"
                gap={2}
            >
                <Box
                    flex={1}
                    display="flex"
                    flexDirection="column"
                    gap={1}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={1}
                    >
                        <Typography
                            variant="h3"
                            color="text.primary"
                            sx={{ fontWeight: 600, mb: 0 }}
                        >
                            Доставка лидов в Telegram
                        </Typography>
                        {isTelegramConnected ? (
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.7}
                                sx={{ mr: 1 }}
                            >
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        background: 'var(--lm-success-ink)',
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500, color: 'var(--lm-success-ink)' }}
                                >
                                    Подключён
                                </Typography>
                            </Box>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleConnectBot}
                                disabled={getBindLinkMutation.isLoading}
                                sx={{ ml: 2 }}
                            >
                                Подключить
                            </Button>
                        )}
                    </Box>
                    {isTelegramConnected && (
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={1}
                        >
                            <CustomSwitch
                                inputProps={{ 'aria-label': 'Уведомления в Telegram' }}
                                checked={notificationsEnabled}
                                onChange={(e) => {
                                    const enabled = e.target.checked;
                                    setNotificationsEnabled(enabled);

                                    // Используем try-catch для лучшей обработки ошибок
                                    (async () => {
                                        try {
                                            console.log('Отправляем запрос на обновление уведомлений:', {
                                                tg_notifications_enabled: enabled,
                                            });

                                            // Обновляем только уведомления, не трогая другие поля
                                            await AccountsService.updateAccount({
                                                tg_notifications_enabled: enabled,
                                            });

                                            console.log('Уведомления успешно обновлены');

                                            // Обновляем глобальный стор для синхронизации с меню
                                            useUserStore.getState().updateNotificationSettings({
                                                tg_notifications_enabled: enabled,
                                            });

                                            // Отправляем событие для обновления анимации в меню
                                            EventService.dispatch('NOTIFICATIONS_UPDATED', { enabled });

                                            enqueueSnackbar(
                                                enabled
                                                    ? 'Уведомления в Telegram включены'
                                                    : 'Уведомления в Telegram выключены',
                                                { variant: 'success' }
                                            );
                                        } catch (error) {
                                            // Возвращаем состояние обратно при ошибке
                                            setNotificationsEnabled(!enabled);
                                            console.error('Ошибка обновления уведомлений:', error);
                                            enqueueSnackbar('Ошибка при обновлении уведомлений', { variant: 'error' });
                                        }
                                    })();
                                }}
                            />
                            <Typography variant="body1">Уведомления в Telegram</Typography>
                        </Box>
                    )}
                    {canSelectMode && (
                        <RadioGroup
                            row
                            value={tgNotificationMode}
                            onChange={handleChangeMode}
                            name="tg-notification-mode"
                        >
                            <FormControlLabel
                                value="all"
                                control={<Radio />}
                                label={<span>Все сообщения</span>}
                            />
                            <Tooltip
                                title={!isAIAvailable ? 'ИИ-отбор сейчас недоступен для вашего аккаунта.' : ''}
                                arrow
                            >
                                <span>
                                    <FormControlLabel
                                        value="relevant"
                                        control={<Radio disabled={!isAIAvailable} />}
                                        label={
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <span style={{ marginRight: 4 }}>
                                                    <AIIcon />
                                                </span>
                                                Только целевые лиды
                                            </span>
                                        }
                                    />
                                </span>
                            </Tooltip>
                        </RadioGroup>
                    )}
                </Box>
            </Box>
        );
    };

    const renderContent = () => (
        <LoadingWrapper isLoading={false}>
            <Box
                pt={2}
                minHeight="100%"
                display="flex"
                flexDirection="column"
            >
                <Box mb={1.5}>
                    <Title>Уведомления</Title>
                </Box>
                {/* --- Блок Telegram --- */}
                {renderTelegramBlock()}
                {/* --- Конец блока Telegram --- */}
            </Box>
        </LoadingWrapper>
    );

    if (!isMobile) {
        return (
            <DesktopLayout hideRightSidebar>
                <Box sx={{ maxWidth: 900 }}>{renderContent()}</Box>
            </DesktopLayout>
        );
    }

    return (
        <WithoutHeader
            showBack
            showMyHeader
            title="Уведомления"
        >
            {renderContent()}
        </WithoutHeader>
    );
}
